import React, { useCallback, useEffect, useRef, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import { useIsRequiredElement } from '../../../hook/useIsRequiredElement';
import ElementErrorMessage from './components/element-error-message';
import { useUserFormRole } from '../../../hook/useUserFormRole';
import { useIsInitFormPage } from '../../../hook/pages/useIsInitFormPage';
import { useIsSubmitFormPage } from '../../../hook/useIsSubmitFormPage';
import { useAuth } from '../../../container/authContext';
import { GridYValue, GridYUnit } from '../../../constants';
import cx from 'classnames';
import { isMobile, isTablet } from 'react-device-detect';
import DateCalendar from '../../calendars/DateCalendar/DateCalendar';
import { HIRE_DATE_FOR_KEY, HIRE_DATE_ON_INITIATION_KEY } from '../../from-builder/elements-settings/components/hire-date-setting';
import { AUTO_ADD_INFO_FOR, SUBMITTER_2_STRICT_ORDER_STEP } from '../../from-builder/elements-settings/submitters-field-element-settings';

// A date of hire ("YYYY-MM-DD") the way the field stores a picked date: noon of that day
// in the browser's timezone, so the calendar day holds wherever the form is opened and
// in the form name and the PDF, which read the UTC date. The backend's
// get_hire_date_value takes noon in the form's timezone.
export const getHireDateValue = (hireDate) => dayjs(hireDate).hour(12).toDate();

const isHireDateValue = (valueInput, hireDate) => dayjs(valueInput).format('YYYY-MM-DD') === hireDate;

export const DateElement = function FromElement({
  element,
  handlerChange,
  error,
  formHasOnlyOneSubmitter,
  previewTabItem,
  strictOrderStep,
  submitterInfo,
  submitter2Info,
}) {
  const userFormRole = useUserFormRole();
  const isSubmitFormPage = useIsSubmitFormPage();
  const isInitFormPage = useIsInitFormPage();
  const user = useAuth()?.user;
  const isRequiredElement = useIsRequiredElement(element, formHasOnlyOneSubmitter);
  // With the label on the left it belongs to a fixed column the constructor
  // also draws, so the width comes from the stylesheet. Above the input it
  // still hugs its own text.
  const labelPlacementLeft = element.options.labelPlacement === 'left';
  const isRequiredForAnotherRole = !!element.options.requiredFor && element.options.requiredFor !== userFormRole;
  const isEditable = element.options?.editable && !element.options.readOnly && (!isRequiredForAnotherRole || formHasOnlyOneSubmitter);
  let value = null;

  if (element.options.valueInput) {
    value = new Date(element.options.valueInput);
  }

  // "Automatically add date-of-hire info for": the initiator's on the initiation page, the
  // submitter's (get-form's `submitter_info`, whoever the form is filled in for) once it
  // reaches them, Submitter 2's at step 2 of a strict order -- before that too when set to
  // "As soon as the form is initiated", from the one person step 2 names (`submitter_2_info`,
  // absent when step 2 is a group). `strictOrderStep` is null without a strict order, which
  // is how "Submitter 2" ends up ignored there. Empty when the person has no date of hire.
  const hireDateFor = element.options[HIRE_DATE_FOR_KEY];
  const isHireDateOnInitiation = Boolean(element.options[HIRE_DATE_ON_INITIATION_KEY]);
  const isAtSubmitter2Step =
    isSubmitFormPage && hireDateFor === AUTO_ADD_INFO_FOR.SUBMITTER_2 && strictOrderStep === SUBMITTER_2_STRICT_ORDER_STEP;
  let hireDate = null;

  if (isInitFormPage && hireDateFor === AUTO_ADD_INFO_FOR.INITIATOR) {
    hireDate = user?.hire_date;
  } else if (isSubmitFormPage && hireDateFor === AUTO_ADD_INFO_FOR.SUBMITTER) {
    hireDate = submitterInfo?.hire_date;
  } else if (isAtSubmitter2Step) {
    hireDate = submitterInfo?.hire_date;
  } else if (
    isSubmitFormPage &&
    hireDateFor === AUTO_ADD_INFO_FOR.SUBMITTER_2 &&
    isHireDateOnInitiation &&
    strictOrderStep != null &&
    strictOrderStep < SUBMITTER_2_STRICT_ORDER_STEP
  ) {
    hireDate = submitter2Info?.hire_date;
  }

  // What step 1 carried in for the person step 2 names was only provisional: step 2's own
  // submitter replaces it. Only while the field still holds what the form was opened with,
  // so a date they pick themselves stays.
  const openedWithValueRef = useRef(element.options.valueInput);
  const replacesProvisionalHireDate =
    isAtSubmitter2Step &&
    isHireDateOnInitiation &&
    !!hireDate &&
    !!element.options.valueInput &&
    element.options.valueInput === openedWithValueRef.current &&
    !isHireDateValue(element.options.valueInput, hireDate);

  useEffect(() => {
    if (hireDate && (!element.options.valueInput || replacesProvisionalHireDate)) {
      handlerChange(element, getHireDateValue(hireDate));
    }
  }, [element, hireDate, replacesProvisionalHireDate]);

  useEffect(() => {
    if (!hireDateFor && !isRequiredForAnotherRole && !isEditable && !element.options.valueInput && (isSubmitFormPage || isInitFormPage)) {
      handlerChange(element, new Date());
    }
  }, [element, hireDateFor, isEditable, isInitFormPage, isRequiredForAnotherRole, isSubmitFormPage]);

  const handleDatePickerFocus = useCallback(() => {
    if (isEditable && !value) {
      const currentDate = new Date();
      handlerChange(element, currentDate);
    }
  }, [element, handlerChange, isEditable, value]);

  const CustomInput = forwardRef(({ value, className, onClick }, ref) => (
    <input
      value={value}
      className={className}
      style={{
        height: element.options.innerElemHeight * GridYValue + GridYUnit,
        zIndex: 5000 + (element.options.zIndex || 10),
        position: 'relative',
        width: '100%',
        minHeight: 'unset',
      }}
      onClick={onClick}
      ref={ref}
      readOnly
    />
  ));
  return (
    <div
      className={cx('form-element-flex-label-wrapper', {
        labelPlacementLeft,
        isMobile: isMobile || previewTabItem === 'phone',
        isTablet: isTablet || previewTabItem === 'tablet',
      })}
    >
      <label
        htmlFor={element.id}
        className="form-label"
        style={{
          zIndex: 5000 + (element.options.zIndex || 10),
          ...(labelPlacementLeft ? {} : { width: 'fit-content' }),
          position: 'relative',
        }}
      >
        {element.options.label} <span className="required">{isRequiredElement ? '*' : ''}</span>
      </label>
      <DateCalendar
        id={element.id}
        readOnly={!isEditable}
        placeholderText={isEditable ? 'Choose Date' : ''}
        selected={element?.options?.valueInput ? new Date(element?.options?.valueInput) : ''}
        onFocus={handleDatePickerFocus}
        customInput={<CustomInput className={`form-control form-control-${element.options.size}`} />}
        selectedDate={element?.options?.valueInput ? new Date(element?.options?.valueInput) : ''}
        handleChange={(date) => (isEditable ? handlerChange(element, date) : undefined)}
        portalPopper
      />
      <ElementErrorMessage message={error} />
      {element.options.instructions && <p className="instructions">{element.options.instructions}</p>}
    </div>
  );
};
